import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type { ManagerAssessmentSkillSelectorOptionValue } from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import type { PaginatedRequestParams, PaginatedResponse } from '@/types/api';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import { api } from '@/api/api';
import { filterOutOtherOption } from '@/utils/question';

export default {
    async getReviewTemplates({
        pageNumber,
        pageSize,
        skipCount,
        meta,
    }: PaginatedRequestParams): Promise<PaginatedResponse<ReviewTemplate>> {
        const params: Record<string, string | number | undefined> = {
            pageNumber,
            pageSize,
            reviewType: meta?.reviewType as string | undefined,
        };
        if (skipCount !== undefined) {
            params.skipCount = skipCount;
        }
        const response = await api.get(`/reviewtemplate/byreviewtype`, {
            params,
        });

        return response.data;
    },

    async getManageReviewTemplates({
        pageNumber,
        pageSize,
        skipCount,
        meta,
    }: PaginatedRequestParams): Promise<PaginatedResponse<ReviewTemplate>> {
        const params: Record<string, string | number | undefined> = {
            pageNumber,
            pageSize,
            reviewType: meta?.reviewType as string | undefined,
        };
        if (skipCount !== undefined) {
            params.skipCount = skipCount;
        }
        const response = await api.get(`/reviewtemplate/manage/byreviewtype`, {
            params,
        });

        return response.data;
    },

    async getAmelioTemplates(reviewType: EvaluationTypeValue): Promise<ReviewTemplate[]> {
        const response = await api.get(`/reviewtemplate/appwide/byreviewtype`, {
            params: { reviewType },
        });

        return response.data;
    },

    async createReviewTemplate(
        reviewType: EvaluationTypeValue,
        skillSelectorOption?: ManagerAssessmentSkillSelectorOptionValue | null,
    ): Promise<ReviewTemplate> {
        const response = await api.post(`/reviewtemplate`, {
            reviewType,
            skillSelectorOption,
        });

        return response.data;
    },

    async deleteReviewTemplate(reviewTemplateId: string): Promise<ReviewTemplate> {
        const response = await api.delete(`/reviewtemplate/${reviewTemplateId}`);

        return response.data;
    },

    async updateReviewTemplate(reviewTemplate: ReviewTemplate): Promise<ReviewTemplate> {
        const payload: ReviewTemplate = {
            ...reviewTemplate,
            questions: reviewTemplate.questions.map((question) => ({
                ...question,
                reviewQuestionOptions: filterOutOtherOption(question.reviewQuestionOptions),
            })),
        };
        const response = await api.put(`/reviewtemplate/${reviewTemplate.id}`, payload);

        return response.data;
    },

    async getReviewTemplate(reviewTemplateId: string): Promise<ReviewTemplate> {
        const response = await api.get(`/reviewtemplate/${reviewTemplateId}`);

        return response.data;
    },

    async getReviewTemplatePreviewQuestions(params?: {
        limit?: number;
    }): Promise<ReviewQuestion[]> {
        const response = await api.get('/reviewtemplate/preview-questions', { params });

        // Endpoint responds with ReviewQuestion[] directly
        return response.data ?? [];
    },
};
